<template>
  <div>
    <BaseAZError v-if="isError" :title="errorMessage"></BaseAZError>
    <div v-else class="experience-container">
      <div>
        <h1 class="mb-5">
          {{ experienceInfo?.specialTopicName }}
        </h1>
        <div class="experience-cover h-103.5">
          <el-image
            style="width: 100%; height: 100%; border-radius: 10px;"
            :src="experienceInfo?.cover"
            :preview-src-list="[experienceInfo!.cover!, ...(experienceInfo?.images?.map(item => item.imageUrl!) || [])]"
            :initial-index="0"
            fit="cover"
            :alt="experienceInfo?.specialTopicName || 'Experience cover image'"
            :title="experienceInfo?.specialTopicName || 'Experience image title'"
            :aria-label="experienceInfo?.specialTopicName || 'Cover image for experience'"            />
          <div v-if="experienceInfo?.images?.length" class="experience-images">
            <el-image
              style="width: 100%; height: 100%; border-radius: 10px;"
              :src="experienceInfo.images[0].imageUrl"
              :preview-src-list="[experienceInfo!.cover!, ...(experienceInfo?.images?.map(item => item.imageUrl!) || [])]"
              :initial-index="1"
              fit="cover"
              :alt="experienceInfo?.specialTopicName || 'Experience image'"
              :title="experienceInfo?.specialTopicName || 'Experience image title'"
              :aria-label="experienceInfo?.specialTopicName || 'image for experience'"  
            />
            <div v-if="experienceInfo.images.length >= 2" class="see-more-image">
              <el-image
                style="width: 100%; height: 100%;"
                :src="experienceInfo.images[1].imageUrl"
                :preview-src-list="[experienceInfo!.cover!, ...experienceInfo.images.map(item => item.imageUrl!)]"
                :initial-index="2"
                fit="cover"
                :alt="experienceInfo?.specialTopicName || 'Experience image'"
                :title="experienceInfo?.specialTopicName || 'Experience image title'"
                :aria-label="experienceInfo?.specialTopicName || 'image for experience'"
              />
              <div v-if="experienceInfo.images.length > 2" class="shadow">
                {{ t('key.experience.view.all.images', [experienceInfo.images.length + 1]) }}
              </div>
            </div>
          </div>
        </div>
        <p class="mb-5 font-300">
          {{ experienceInfo?.intro }}
        </p>
      </div>
      <!-- 筛选条件 -->
      <div class="flex space-x-5" m="t-8 b-6">
        <SelectDate class="mr-2 flex-1" :attr-name="experienceInfo?.specialTopicName ?? ''" />
        <Filter />
      </div>

      <div class="space-y-4">
        <div v-for="(supplier, supplierIndex) in supplierList" :key="supplier.id" class="supplier-item">
          <div class="supplier-info">
            <div class="supplier-left">
              <div flex="~ col justify-center">
                <div h="12" flex="~ items-center">
                  <img :src="supplier.logo" w="30" />
                </div>
                <div
                  v-if="!supplier.showAll && !supplier.showMore"
                  class="show-more" color="#888888" @click="showMoreHandler(supplier)"
                >
                  <ElIcon><ElIconArrowDown></ElIconArrowDown></ElIcon>
                  <van-highlight
                    decoration="underline"
                    :keywords="t('key.attractions.num.more', [supplier.details.length > defaultExpandLength ? defaultExpandLength : supplier.details.length])"
                    :source-string="t('key.attraction.detail.price.moreOptions', [t('key.attractions.num.more', [supplier.details.length > defaultExpandLength ? defaultExpandLength : supplier.details.length])])"
                    highlight-class="font-700"
                  />
                </div>
              </div>
              <div class="supplier-middle">
                <div v-if="experienceInfo?.cashbackDesc" flex="~ row">
                  <img :src="iconGift" w="5" h="5" />
                  <p
                    class="truncate"
                    :style="{ color: 'var(--tc-color-cashback-desc)' }"
                    m="l-1"
                    font="500"
                    text="sm"
                    max-w="80"
                  >
                    {{ experienceInfo?.cashbackDesc }}
                  </p>
                </div>
                <div class="supplier-price">
                  {{ getPrice(supplier.price) }}
                </div>
              </div>
            </div>
            <ElButton type="primary" size="large" @click="viewDeal(supplier.details[0], supplierIndex, 0, true)">
              <span text="lg">{{ t('key.attraction.detail.price.viewDeal') }}</span>
              <ElIcon><ElIconArrowRight></ElIconArrowRight></ElIcon>
            </ElButton>
          </div>
          <div v-show="supplier.showAll || supplier.showMore" class="ticket-list">
            <div
              v-for="(item, productIndex) in supplier.showAll ? supplier.details : supplier.details.slice(0, defaultExpandLength)"
              :key="item.attSourcePkId"
              class="ticket-item"
            >
              <div class="sku-left">
                <div class="sku-title">
                  {{ item.productTitle }}
                  <van-icon v-if="item.productDescription" name="info-o" class="info-icon" @click="showSkuDesc(item)" />
                </div>
                <div class="sku-price">
                  {{ getPrice(item.price) }}
                </div>
              </div>
              <div class="flex cursor-pointer select-none items-center" :style="{ color: 'var(--tc-color-primary)' }" @click="viewDeal(item, supplierIndex, productIndex, false)">
                {{ t('key.attraction.detail.price.viewDeal') }}
                <ElIcon><ElIconArrowRight></ElIconArrowRight></ElIcon>
              </div>
            </div>
            <div class="mt-4 inline-flex cursor-pointer select-none items-center gap-1 color-#888888" @click="showLessOrShowAll(supplier)">
              <template v-if="!supplier.showAll">
                <ElIcon><ElIconArrowDown></ElIconArrowDown></ElIcon>
                <span>{{ t('key.attraction.detail.price.allOptions') }}</span>
              </template>
              <template v-else>
                <ElIcon><ElIconArrowUp></ElIconArrowUp></ElIcon>
                <span>{{ t('key.attraction.detail.price.lessOptions') }}</span>
              </template>
            </div>
          </div>
        </div>
        <Empty v-if="!supplierList.length" :show-btn="false" desc=""></Empty>
      </div>
    </div>
    <ElDialog
      v-model="skuDescVisible" :title="t('key.experience.detail.sku.desc')"
      width="650px"
      align-center
    >
      <div class="experience-desc" v-html="currentSku?.productDescription"></div>
    </ElDialog>
  </div>
</template>

<script lang="ts" setup>
import SelectDate from '../components/SelectDate.vue'
import Filter from '../components/Filter.vue'

import iconGift from '@/assets/images/list/icon_gift.png'

const {
  experienceInfo,
  supplierList,
  isError,
  errorMessage,
  defaultExpandLength,
  currentSku,
  skuDescVisible,
  viewDeal,
  showLessOrShowAll,
  showMoreHandler,
  getPrice,
  showSkuDesc,
} = useExperienceDetail()

const { t } = useI18n()
</script>

<style lang="scss" scoped>
.experience-container {
  margin: 20px auto;
  padding: 0 20px;
  padding-bottom: 40px;
  width: 100%;
  max-width: 1240px;
}
.supplier-item {
  padding: 38px;
  border: 1px solid #D9D9D9;
  border-radius: 15px;
}

.experience-cover {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
  gap: 20px;
  >img {
    flex: 1;
    object-fit: cover;
  }
  .experience-images {
    width: 286px;
    display: flex;
    flex-direction: column;
    gap: 20px;
    > img {
      object-fit: cover;
      &:first-child {
        height: 0;
        flex: 1;
      }
    }
    .see-more-image {
      position: relative;
      height: 117px;
      border-radius: 10px;
      overflow: hidden;
      > img {
        width: 100%;
        object-fit: cover;
      }
      .shadow {
        position: absolute;
        top: 0;
        left: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.3);
        z-index: 1;
        color: #fff;
        font-size: 16px;
        cursor: pointer;
        pointer-events: none;
      }
    }
    &:hover {
      .shadow {
        background-color: rgba(0, 0, 0, 0.5);
      }
    }
  }
}

.supplier-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 45px;
  .supplier-left {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 20px;
    justify-content: space-between;
  }
  .supplier-middle {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }
  .supplier-price {
    font-size: 26px;
    font-weight: 500;
  }
  .show-more {
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
  }
}

.ticket-list {
  margin-top: 10px;
  padding: 0 20px;
  .ticket-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid #D9D9D9;
    gap: 90px;
    .sku-left {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 80px;
      justify-content: space-between;
    }
    .sku-title {
      flex: 1;
      .info-icon {
        margin-left: 10px;
        cursor: pointer;
      }
    }
    .sku-price {
      font-weight: 500;
      font-size: 16px;
    }
  }
}
</style>

<style>
:root:root {
  --van-highlight-tag-color: #888888;
  --van-swipe-indicator-margin:24px;
}

.experience-desc img {
  max-width: 100%;
}
</style>
